import React, { useRef, useState } from 'react'
import { api } from '../api'
import { Category, RawMaterial } from '../types'

interface Props {
  rawMaterials: RawMaterial[]
  setRawMaterials?: React.Dispatch<React.SetStateAction<RawMaterial[]>>
  categories: Category[]
  onRefresh: () => Promise<void> | void
  onOpenReport?: (item: RawMaterial) => void
}

function PieChartIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

export function RawMaterialsView({ rawMaterials, setRawMaterials, categories, onRefresh, onOpenReport }: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingItem, setEditingItem] = useState<RawMaterial | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const [localHiddenIds, setLocalHiddenIds] = useState<Set<string>>(new Set())

  // Search and Category Filter
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const filteredRawMaterials = React.useMemo(() => {
    return rawMaterials.filter((item) => {
      if (localHiddenIds.has(item.id)) return false
      const term = searchTerm.trim().toLowerCase()
      const matchSearch =
        !term ||
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.code && item.code.toLowerCase().includes(term))
      const matchCategory = !selectedCategory || item.category_id === selectedCategory
      return matchSearch && matchCategory
    })
  }, [rawMaterials, searchTerm, selectedCategory, localHiddenIds])

  // Add Form
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [unit, setUnit] = useState('Kg')
  const [barcode, setBarcode] = useState('')
  const [maliyet, setMaliyet] = useState('')
  const [description, setDescription] = useState('')

  // Edit Form
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const [editCategoryId, setEditCategoryId] = useState('')
  const [editUnit, setEditUnit] = useState('Kg')
  const [editBarcode, setEditBarcode] = useState('')
  const [editMaliyet, setEditMaliyet] = useState('')
  const [editDescription, setEditDescription] = useState('')

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const created = await api.createRawMaterial({
        code,
        name,
        category_id: categoryId || null,
        unit,
        barcode: barcode || null,
        maliyet: parseFloat(maliyet) || 0,
        description: description || null,
      })
      if (setRawMaterials) {
        setRawMaterials((prev) => [created, ...prev])
      } else {
        await onRefresh()
      }
      setShowAddModal(false)
      setCode('')
      setName('')
      setUnit('Kg')
      setBarcode('')
      setMaliyet('')
      setDescription('')
    } catch (err: any) {
      setError(err.message)
    }
  }

  const openEditModal = (item: RawMaterial) => {
    setEditingItem(item)
    setEditCode(item.code)
    setEditName(item.name)
    setEditCategoryId(item.category_id || '')
    setEditUnit(item.unit || 'Kg')
    setEditBarcode(item.barcode || '')
    setEditMaliyet(String(item.maliyet))
    setEditDescription(item.description || '')
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingItem) return
    setError(null)
    try {
      const updated = await api.updateRawMaterial(editingItem.id, {
        code: editCode,
        name: editName,
        category_id: editCategoryId || null,
        unit: editUnit,
        barcode: editBarcode || null,
        maliyet: parseFloat(editMaliyet) || 0,
        description: editDescription || null,
      })
      setEditingItem(null)
      if (setRawMaterials) {
        setRawMaterials((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Bu hammaddeyi silmek istediğinize emin misiniz?')) return
    const itemToDelete = rawMaterials.find((item) => item.id === id)
    const originalIndex = rawMaterials.findIndex((item) => item.id === id)

    if (setRawMaterials) {
      setRawMaterials((prev) => prev.filter((item) => item.id !== id))
    }
    setLocalHiddenIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteRawMaterial(id)
    } catch (err: any) {
      if (setRawMaterials && itemToDelete) {
        setRawMaterials((prev) => {
          if (prev.some((item) => item.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExportExcel = async () => {
    setIsProcessing(true)
    try {
      await api.exportRawMaterialsExcel()
    } catch (err: any) {
      alert(`Excel indirme hatası: ${err.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const confirmed = window.confirm(
      'Excel dosyasındaki hammaddeler sisteme aktarılacaktır.\n\n' +
      '• Eşleşen hammaddeler güncellenecek, yeni hammaddeler eklenecektir.\n' +
      '• Excel dosyasında yer almayan mevcut hammaddeler silinmeyecektir (korunacaktır).\n\n' +
      'Devam etmek istiyor musunuz?'
    )
    if (!confirmed) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setIsProcessing(true)
    setError(null)
    try {
      const res = await api.importRawMaterialsExcel(file)
      alert(
        `Excel içe aktarma başarılı!\n\n` +
        `Yeni Eklenen: ${res.created}\n` +
        `Güncellenen: ${res.updated}\n` +
        `Silinen: ${res.deleted}\n` +
        `Toplam İşlenen: ${res.total_processed}`
      )
      await onRefresh()
    } catch (err: any) {
      setError(`Excel yükleme hatası: ${err.message}`)
    } finally {
      setIsProcessing(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Hammaddeler</h2>
        </div>
        <div className="header-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />
          <button
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            title="Excel dosyasından içe aktar (tam liste değişimi)"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Excel Yükle</span>
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExportExcel}
            disabled={isProcessing || rawMaterials.length === 0}
            title="Mevcut listeyi Excel formatında indir"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Excel İndir</span>
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Yeni Hammadde
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Hammadde adı veya koduna göre ara…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <select
          className="form-control"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '220px' }}
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="table-card">
        {filteredRawMaterials.length === 0 ? (
          <div className="empty-state">
            <p>{rawMaterials.length === 0 ? 'Kayıtlı hammadde bulunmuyor.' : 'Arama kriterlerine uygun hammadde bulunamadı.'}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>KOD</th>
                <th>HAMMADDE ADI</th>
                <th>KATEGORİ</th>
                <th>BİRİM</th>
                <th>BİRİM MALİYET</th>
                <th>AÇIKLAMA</th>
                <th>İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {filteredRawMaterials.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.code}</strong></td>
                  <td>{item.name}</td>
                  <td>{item.category?.name || <span className="text-muted">—</span>}</td>
                  <td><span className="badge badge-secondary">{item.unit || 'Kg'}</span></td>
                  <td>₺{Number(item.maliyet).toFixed(2)}</td>
                  <td>{item.description || <span className="text-muted">—</span>}</td>
                  <td>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => onOpenReport?.(item)}
                        title="Hammadde Raporu"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        <PieChartIcon />
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(item)}>
                        Düzenle
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item.id)}>
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Yeni Hammadde Ekle</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Hammadde Kodu *</label>
                    <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="Örn: HM-001" />
                  </div>
                  <div className="form-group">
                    <label>Hammadde Adı *</label>
                    <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Titanyum Çubuk" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Kategori</label>
                    <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                      <option value="">Kategori Seçin...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Ölçü Birimi</label>
                    <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                      <option value="Kg">Kg</option>
                      <option value="Adet">Adet</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Birim Alış Maliyeti (₺) *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={maliyet}
                      onChange={(e) => setMaliyet(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Açıklama</label>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Hammadde özellikleri..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>İptal</button>
                <button type="submit" className="btn btn-primary">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Hammaddeyi Düzenle</h3>
              <button className="modal-close" onClick={() => setEditingItem(null)}>✕</button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Hammadde Kodu *</label>
                    <input required value={editCode} onChange={(e) => setEditCode(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Hammadde Adı *</label>
                    <input required value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Kategori</label>
                    <select value={editCategoryId} onChange={(e) => setEditCategoryId(e.target.value)}>
                      <option value="">Kategori Seçin...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Ölçü Birimi</label>
                    <select value={editUnit} onChange={(e) => setEditUnit(e.target.value)}>
                      <option value="Kg">Kg</option>
                      <option value="Adet">Adet</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Birim Alış Maliyeti (₺) *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={editMaliyet}
                      onChange={(e) => setEditMaliyet(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Açıklama</label>
                  <input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingItem(null)}>İptal</button>
                <button type="submit" className="btn btn-primary">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
